import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import EActivitiesListSkeleton from '@/components/everridge/activities/EActivitiesListSkeleton';
import OrdersInfoListEmpty from '@/features/orders/components/OrdersInfo/OrdersInfoListEmpty';
import { ordersUtils } from '@/features/orders/utils/ordersUtils';
import EActivitiesElement from '@/components/everridge/activities/EActivitiesElement';
import { DateTimeHelper } from '@/utils/dateTimeHelper';
import {
	OrderListItemResponseDto,
	OrderListItemResponseDtoListResponseDto,
	OrderStatus,
} from '@/features/orders/types/orders-dto';
import { format } from 'date-fns';
import { ETablePaginationWithApi } from '@/components/everridge/table/components/ETablePaginationWithApi';

type Props = {
	data?: OrderListItemResponseDtoListResponseDto;
	isLoading?: boolean;
	page: number;
	setPage: (page: number) => void;
	paginationStep?: number;
	showDetails?: (id: string, status: OrderStatus) => void;
};

const OrdersTableMobile: FC<Props> = ({
	page,
	setPage,
	paginationStep = 10,
	data,
	isLoading,
	showDetails,
}) => {
	const { t } = useTranslation();

	const groupedActivities =
		data?.items?.reduce((acc, curr) => {
			const dateKey = format(new Date(curr.documentDate), 'yyyy-MM-dd');

			if (!acc[dateKey]) {
				acc[dateKey] = [];
			}
			acc[dateKey].push(curr);

			return acc;
		}, {} as Record<string, OrderListItemResponseDto[]>) || [];

	return (
		<div className='grow flex flex-col gap-4'>
			<div className='flex-1'>
				{isLoading && <EActivitiesListSkeleton isMobile={true} />}
				{!isLoading && !!data?.items?.length && (
					<div className='flex flex-col gap-4'>
						{Object.entries(groupedActivities).map(
							([title, events]) => {
								return (
									<div key={title}>
										<h5 className='text-sm text-popover-800'>
											{DateTimeHelper.formatDate(
												new Date(title),
												2,
											)}
										</h5>
										{events.map((event) => {
											const { label, icon, colorClass } =
												ordersUtils.getStatusData(
													event.status,
												);

											return (
												<EActivitiesElement
													key={event.id}
													icon={icon}
													colorClass={colorClass}
													header={
														event.services ??
														t(
															'main.orders.components.list.noServices',
														)
													}
													description={label}
													value={event.totalAmount}
													id={event.id}
													onClick={(id) =>
														showDetails?.(
															id,
															event.status,
														)
													}
													isMobile={true}
													currencyAssetType='other'
												/>
											);
										})}
									</div>
								);
							},
						)}
					</div>
				)}
				{!isLoading && !data?.items?.length && <OrdersInfoListEmpty />}
			</div>
			{!!data?.totalCount && data?.totalCount > 10 && (
				<ETablePaginationWithApi
					totalCount={Math.ceil(data?.totalCount / paginationStep)}
					page={page}
					setPage={setPage}
				/>
			)}
		</div>
	);
};

export default OrdersTableMobile;
